
/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');
const customParseFormat = require('dayjs/plugin/customParseFormat');

// Logical "live" default tokens a descriptor's `Default` may carry instead of a
// concrete value. They resolve to the current date/time when the form loads.
// Kept in one place so the date/time/datetime input types don't each carry their
// own copy of the list — route every check through isSpecialDefaultValue().
const SPECIAL_DEFAULT_VALUES = [ 'now', 'today' ];

/**
 * @typedef {Object} FlatpickrOptions
 * @property {boolean} AllowInput - Whether to allow manual input.
 * @property {string} DayJSDateFormat - The date format string, will be converted to Flatpickr format. (assumes standard DayJS formatting, e.g., 'YYYY-MM-DD')
 * @property {string} DefaultValue - The default value for the date input.
 * @property {boolean} DisablePicker - Whether to disable the date picker.
 * @property {string} HTMLID - The HTML ID of the input element.
 * @property {string} ISODateString - The date value in ISO format.
 * @property {string} PickerType - The type of picker (e.g., 'date', 'datetime', or 'time').
 * @property {string} Timezone - The timezone for the date input.
 * @property {boolean} [Time24Hour] - Whether to use 24-hour time format (only applicable for time or datetime pickers).
 * @property {string} [HiddenInputSelector] - The HTML ID of the hidden input element that stores the ISO value
 */

class FlatpickrUtilProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// Extend dayJS with customParseFormat plugin work with Safari
		if (this.fable.Dates?.dayJS && !this.fable.Dates.dayJS.customParseFormatExtended)
		{
			this.fable.Dates.dayJS.extend(customParseFormat);
			this.fable.Dates.dayJS.customParseFormatExtended = true;
		}
	}

	/**
	 * Initialize the Flatpickr plugin for the input element.
	 * @param {FlatpickrOptions} pFlatpickrOptions The Flatpickr options.
	 * @return {void}
	 */
	initializeFlatpickr(pFlatpickrOptions)
	{
		if (!window.flatpickr)
		{
			console.error('Flatpickr library is not loaded.');
			return;
		}
		if (pFlatpickrOptions.DisablePicker)
		{
			console.info('Flatpickr initialization skipped because the picker is disabled.');
			return;
		}
		if (!pFlatpickrOptions.HTMLID)
		{
			console.error('Flatpickr initialization failed: HTMLID is required.');
			return;
		}
		// If the target input is a Read-theme static display (the Tier-2
		// a11y swap), don't attach the picker — the field is meant to be a
		// static, focusable, copyable display, not an interactive control.
		// We also tear down any prior Flatpickr instance from an earlier
		// render pass (e.g., the form transitioned Edit → Read at runtime),
		// since simply returning would leave the stale picker still
		// listening for focus / click.
		//
		// Discriminate ONLY on `aria-readonly="true"`: that is the marker
		// the Read-theme templates emit, and Flatpickr never sets it. We
		// must NOT also key off a bare `readonly` attribute — Flatpickr
		// itself stamps `readonly` on the input whenever it initializes with
		// allowInput:false, which this provider does on touch devices (see
		// tmpIsTouchDevice below). Treating that Flatpickr-set `readonly` as
		// a Read-theme signal would destroy a live, legitimately-initialized
		// Edit-mode picker on the very next marshal, leaving every date/time
		// field dead on tablets/phones after the first interaction.
		const tmpTargetElement = document.querySelector(pFlatpickrOptions.HTMLID);
		if (tmpTargetElement && tmpTargetElement.getAttribute('aria-readonly') === 'true')
		{
			if (tmpTargetElement['_flatpickr'] && typeof tmpTargetElement['_flatpickr'].destroy === 'function')
			{
				tmpTargetElement['_flatpickr'].destroy();
			}
			console.info(`Flatpickr initialization skipped for [${pFlatpickrOptions.HTMLID}] because the input is aria-readonly (Read-theme static display).`);
			return;
		}
		// Don't tear down a picker the user is actively using. A re-marshal can
		// fire mid-interaction: clicking the hour/minute spinner arrows or the
		// AM/PM toggle dispatches a native `change` on the display input (Flatpickr
		// updateTime -> _debouncedChange -> triggerEvent), which routes through the
		// data request -> solve -> marshal -> back into here. Re-initializing calls
		// window.flatpickr(), which destroys and recreates the instance, slamming
		// the open picker shut. Only skip re-init when the calendar is genuinely
		// rendered and visible — never for a stale/invisible "open" flag, so any bad
		// state always self-heals on the next marshal instead of getting stuck.
		const tmpExistingInstance = tmpTargetElement ? tmpTargetElement['_flatpickr'] : null;
		const tmpOpenCalendar = tmpExistingInstance ? tmpExistingInstance.calendarContainer : null;
		const tmpPickerIsOpen = !!tmpExistingInstance &&
			tmpExistingInstance.isOpen === true &&
			!!tmpOpenCalendar &&
			tmpOpenCalendar.classList.contains('open') &&
			tmpOpenCalendar.getBoundingClientRect().height > 0;
		if (tmpPickerIsOpen)
		{
			return;
		}
		// Define picker type map for resuable settings
		const pickerTypeMap = {
			date: {
				displayName: 'Date',
				flatpickrDateFormat: 'Y-m-d',
				dayJSDateFormat: 'YYYY-MM-DD',
				icon: 'fa-calendar',
			},
			datetime: {
				displayName: 'Date & Time',
				flatpickrDateFormat: 'Y-m-d H:i',
				dayJSDateFormat: 'YYYY-MM-DD HH:mm',
				icon: 'fa-calendar-clock',
			},
			time: {
				displayName: 'Time',
				flatpickrDateFormat: 'H:i',
				dayJSDateFormat: 'HH:mm',
				icon: 'fa-clock',
			},
		};
		const tmpPickerType = pFlatpickrOptions.PickerType?.toLowerCase() || 'date';
		const tmpHTMLID = pFlatpickrOptions.HTMLID;
		const tmpDayJSFormat = pFlatpickrOptions.DayJSDateFormat || pickerTypeMap[tmpPickerType]?.dayJSDateFormat || 'YYYY-MM-DD';
		const tmpISODateString = pFlatpickrOptions.ISODateString || '';
		const tmpDefaultValue = pFlatpickrOptions.DefaultValue || '';
		const tmpRequestedAllowInput = pFlatpickrOptions.AllowInput !== undefined ? pFlatpickrOptions.AllowInput : true;
		// Detect touch-primary devices so we can suppress manual keyboard entry on
		// mobile. Without this guard, iOS Safari opens the soft keyboard the
		// instant the user taps a date field, then the Flatpickr calendar overlay
		// (now forced on mobile via disableMobile: true) renders on top of the
		// keyboard — a screen-real-estate fight that hides part of the calendar
		// and confuses users about which UI is authoritative. The (maxTouchPoints
		// > 1) clause is intentional: touch-enabled laptops report 1 only for a
		// trackpad, so we don't accidentally strip typing from Surface/iPad-with-
		// keyboard users who do have a real keyboard attached. Explicit
		// AllowInput: false from callers is still honored everywhere.
		const tmpIsTouchDevice = (typeof navigator !== 'undefined') && (((navigator.maxTouchPoints || 0) > 1) || ('ontouchstart' in window));
		const tmpAllowInput = tmpRequestedAllowInput && !tmpIsTouchDevice;
		// Resolve timezone defensively: an invalid or unset timezone otherwise propagates
		// into dayjs.tz() and throws an exception that Flatpickr's init swallows silently.
		let tmpTimezone;
		let isDifferentTimezone = false;
		let timeZoneDisplay = '';
		try
		{
			tmpTimezone = this.resolveTimezone(pFlatpickrOptions.Timezone);
			const tmpCurrentTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
			isDifferentTimezone = tmpTimezone && (tmpTimezone !== tmpCurrentTimezone);
			if (isDifferentTimezone)
			{
				const hourDifference = this.pict.Dates.dayJS().tz(tmpTimezone).utcOffset() - this.pict.Dates.dayJS().tz(tmpCurrentTimezone).utcOffset();
				timeZoneDisplay = tmpTimezone + ' ' + (hourDifference > 0 || hourDifference < 0 ? `(+${Math.abs(hourDifference / 60)} hrs)` : `(-${Math.abs(hourDifference / 60)} hrs)`);
			}
		}
		catch (err)
		{
			console.warn(`FlatpickrUtilProvider: timezone resolution failed during init, continuing with browser-local. Reason: ${err?.message}`);
			tmpTimezone = undefined;
			isDifferentTimezone = false;
			timeZoneDisplay = '';
		}
		const enableTime = (tmpPickerType === 'datetime' || tmpPickerType === 'time');
		// Capture provider reference for use in Flatpickr event handler closures
		const self = this;
		// Convert ISO/default value to a fake-local Date that Flatpickr will display as document-TZ time
		const tmpDefaultDate = this.pict.providers.FlatpickrUtilProvider.toFlatpickrDate(tmpISODateString || tmpDefaultValue, tmpTimezone);
		// track manual time input changes for auto-save on close
		// store on the element to persist across re-initializations
		const tmpElement = document.querySelector(tmpHTMLID);
		if (tmpElement && (tmpElement).hasOwnProperty('_hasTimeChanges') === false)
		{
			tmpElement['_hasTimeChanges'] = false;
		}
		// Reset _userCleared on each initialization (marshal pushes fresh state)
		if (tmpElement)
		{
			tmpElement['_userCleared'] = false;
		}
		// Preview-only flag: when the picker is seeded with "now" on open, this tracks
		// whether the user has explicitly interacted. If still true on close, we revert.
		if (tmpElement && !tmpElement.hasOwnProperty('_previewOnly'))
		{
			tmpElement['_previewOnly'] = false;
		}
		// store the hidden input ID on element for use by clearDatePicker/setDatePickerToNow
		if (tmpElement && pFlatpickrOptions.HiddenInputSelector)
		{
			tmpElement['_hiddenInputID'] = pFlatpickrOptions.HiddenInputSelector;
		}
		// NOTE: Flatpickr Plugin options and documentation: https://flatpickr.js.org/options/
		const tmpConfig = {
			dateFormat: this.convertDayJSToFlatpickrFormat(tmpDayJSFormat),
			allowInput: tmpAllowInput,
			defaultDate: tmpDefaultDate,
			enableTime: enableTime,
			noCalendar: (tmpPickerType === 'time'),
			time_24hr: pFlatpickrOptions.Time24Hour !== undefined ? pFlatpickrOptions.Time24Hour : true,
			// Force the Flatpickr UI on mobile devices instead of falling back to the
			// browser's native date/time picker. By default, Flatpickr detects mobile
			// user agents and silently swaps our <input type="text"> for a native
			// type="date" / "time" / "datetime-local", which causes iOS Safari to show
			// its OS UIDatePicker. That native path bypasses every lifecycle hook in
			// this provider — including the fake-local Date pattern (toFlatpickrDate /
			// fromFlatpickrDate) responsible for document-timezone correctness — so
			// dates picked on mobile would be written back without the TZ adjustment
			// the desktop flow applies. Keeping the Flatpickr UI on mobile also
			// ensures the Clear / "Set to Current" extras and the timezone indicator
			// render consistently across devices.
			disableMobile: true,
			// customize the picker UI after it's ready
			onReady: function(dateObj, dateStr, instance)
			{
				const template =
				`<div class="flatpicker-extras">
					${ isDifferentTimezone ? `<div class="timezone-indicator input is-static is-fullwidth is-small"><span class="has-text-text-50">Times shown in</span> ${timeZoneDisplay}</div>` : '' }
					<div class="buttons py-1 is-centered mb-0">
						<button class="button is-outline is-small" type="button" onclick="_Pict.providers.FlatpickrUtilProvider.clearDatePicker('${tmpHTMLID}')">
							<span class="icon">
								<i class="fa fa-fw fa-undo"></i>
							</span>
							<span class="icon-text">
								Clear
							</span>
						</button>
						<button class="button is-link is-small" type="button" onclick="_Pict.providers.FlatpickrUtilProvider.setDatePickerToNow('${tmpHTMLID}')">
							<span class="icon">
								<i class="fa fa-fw ${ pickerTypeMap[tmpPickerType]?.icon || 'fa-calendar'}"></i>
							</span>
							<span class="icon-text">
								Set to Current ${ pickerTypeMap[tmpPickerType]?.displayName || 'Date' }
							</span>
						</button>
					</div>
				</div>`;
				if (instance?.calendarContainer)
				{
					// add the extra buttons to the calendar container
					instance.calendarContainer.insertAdjacentHTML('beforeend', template);
				}
				// listeners for time picker manual inputs (hour, minute, AM/PM) to detect changes
				// only needed for time/datetime pickers - date picker saves automatically
				if (instance?.calendarContainer && enableTime)
				{
					const timeInputs = instance.calendarContainer.querySelectorAll('.flatpickr-hour, .flatpickr-minute');
					timeInputs.forEach(function(input)
					{
						input.addEventListener('input', function()
						{
							instance.input._hasTimeChanges = true;
							// User explicitly touched time inputs — no longer a preview
							instance.input._previewOnly = false;
						});
					});
					// Also track AM/PM toggle clicks!
					const ampmToggle = instance.calendarContainer.querySelector('.flatpickr-am-pm');
					if (ampmToggle)
					{
						ampmToggle.addEventListener('click', function()
						{
							instance.input._hasTimeChanges = true;
							// User explicitly toggled AM/PM — no longer a preview
							instance.input._previewOnly = false;
						});
					}
					// Close picker on Tab key ONLY when tabbing past the last time element.
					// Flatpickr only auto-parses typed input on Enter — Tab bypasses that,
					// so we parse it ourselves via setDate before closing.
					// In 12-hour mode the tab order is: input → hour → minute → AM/PM → close.
					// In 24-hour mode the tab order is: input → hour → minute → close.
					if (!tmpElement['_tabKeyHandler'])
					{
						tmpElement['_tabKeyHandler'] = function(event)
						{
							if (event.key !== 'Tab')
							{
								return;
							}
							const currentInstance = tmpElement?.['_flatpickr'];
							if (!currentInstance?.isOpen)
							{
								return;
							}
							const container = currentInstance.calendarContainer;
							if (!container)
							{
								return;
							}
							const target = event.target;
							// Determine the last focusable time element based on 12/24-hour mode
							const ampmToggle = container.querySelector('.flatpickr-am-pm');
							const minuteInput = container.querySelector('.flatpickr-minute');
							const lastTimeElement = ampmToggle || minuteInput;
							// Determine the first focusable time element (hour input)
							const hourInput = container.querySelector('.flatpickr-hour');
							const firstTimeElement = hourInput || currentInstance.input;
							// Only close when tabbing forward past the last element
							// or shift-tabbing before the first element (back to the main input)
							const isTabbingPastEnd = !event.shiftKey && (target === lastTimeElement);
							const isTabbingBeforeStart = event.shiftKey && (target === firstTimeElement || target === currentInstance.input);
							if (isTabbingPastEnd || isTabbingBeforeStart)
							{
								event.preventDefault();
								// Parse any manually typed value so selectedDates reflect what the user typed
								const inputValue = currentInstance.input?.value?.trim();
								if (inputValue)
								{
									// triggerChange=true fires onChange, which resets _hasTimeChanges
									// and dispatches the data request via the change event
									currentInstance.setDate(inputValue, true);
								}
								currentInstance.close();
								// Move focus to the next/previous focusable element outside the picker
								if (!event.shiftKey)
								{
									currentInstance.input.focus();
								}
							}
						};
						// Listen on document (capture phase) so we intercept Tab inside the
						// calendar container's time inputs, not just on the main input element
						document.addEventListener('keydown', tmpElement['_tabKeyHandler'], true);
					}
					// handle outside click/touch to close picker (known Flatpickr bug with allowInput + enableTime)
					if (tmpAllowInput && !tmpElement['_outsideClickHandler'])
					{
						tmpElement['_outsideClickHandler'] = function(event)
						{
							// get the current element and instance
							const currentElement = document.querySelector(tmpHTMLID);
							if (!currentElement)
							{
								return;
							}
							const currentInstance = currentElement['_flatpickr'];
							// check if picker is open using classList (isOpen property can be unreliable)
							const isPickerOpen = currentInstance?.calendarContainer?.classList.contains('open');
							if (!currentInstance || !isPickerOpen)
							{
								return;
							}
							const target = event.target;
							const isInsidePicker = currentInstance.calendarContainer?.contains(target);
							const isInput = currentInstance.input === target || currentInstance.input?.contains(target);
							if (!isInsidePicker && !isInput)
							{
								currentInstance.close();
							}
						};
						document.addEventListener('mousedown', tmpElement['_outsideClickHandler']);
						// Passive touchstart: the handler never calls preventDefault, and on
						// iOS Safari non-passive touchstart listeners block scroll/gesture
						// compositing while they run — a noticeable jank source on long forms.
						document.addEventListener('touchstart', tmpElement['_outsideClickHandler'], { passive: true });
					}
				}
			},
			onOpen: function(selectedDates, dateStr, instance)
			{
				// For time-enabled pickers: if the input is empty and hasn't been explicitly
				// cleared by the user, seed the picker UI with "now" in document timezone
				// as a visual preview. The value won't commit to the input field unless the
				// user explicitly interacts (clicks a date, edits time, clicks "Set to Current",
				// or presses Enter). If they close without interacting, we revert.
				if (enableTime && selectedDates.length === 0 && !instance.input['_userCleared'])
				{
					const fakeLocalNow = self.toFlatpickrDate('now', tmpTimezone);
					if (fakeLocalNow)
					{
						// false = don't trigger onChange, this is just a UI preview
						instance.setDate(fakeLocalNow, false);
						instance.input['_previewOnly'] = true;
						// Clear the display input so it still appears empty / shows placeholder
						instance.input.value = '';
					}
				}
				// On touch devices, scroll the input near the top of the viewport so
				// the calendar overlay has room to render below it without being
				// clipped — especially important for datetime and time pickers,
				// which are taller and where the iOS soft keyboard can still surface
				// for the in-picker hour/minute inputs. setTimeout defers the scroll
				// until after Flatpickr has placed its calendar, so Flatpickr's own
				// scroll-reposition handler runs once with the input in its final
				// resting spot rather than chasing a moving target.
				if (tmpIsTouchDevice)
				{
					setTimeout(function()
					{
						const tmpRect = instance.input.getBoundingClientRect();
						// Target ~80px from the top of the viewport — keeps the
						// field label visible above and frees maximum vertical
						// space below for the calendar (~340px for date-only,
						// taller for datetime/time pickers).
						const tmpTargetOffsetFromTop = 80;
						const tmpScrollDelta = tmpRect.top - tmpTargetOffsetFromTop;
						// Only scroll when we'd move more than a few pixels to
						// avoid jitter if the input is already well-placed.
						if (Math.abs(tmpScrollDelta) > 8)
						{
							window.scrollBy({ top: tmpScrollDelta, behavior: 'smooth' });
						}
					}, 50);
				}
			},
			onChange: function(selectedDates, dateStr, instance)
			{
				if (selectedDates.length > 0)
				{
					// No offset math needed — Flatpickr now operates in document-TZ context.
					// The picker displays fake-local dates (via toFlatpickrDate), and the providers'
					// onDataRequest methods use fromFlatpickrDate to interpret the result correctly.
					if (enableTime)
					{
						// reset time changes flag for time/datetime pickers
						instance.input['_hasTimeChanges'] = false;
					}
					// User explicitly selected a value — no longer a preview
					instance.input['_previewOnly'] = false;
					// Clear the user-cleared flag since a value is now selected
					instance.input['_userCleared'] = false;
				}
			},
			onClose: function(selectedDates, dateStr, instance)
			{
				// If the picker was seeded with a preview "now" and the user never
				// interacted, revert — don't commit the previewed value.
				if (instance.input['_previewOnly'])
				{
					instance.clear();
					instance.input['_previewOnly'] = false;
					instance.input['_hasTimeChanges'] = false;
					return;
				}
				// auto-save any pending manual time changes when closing the picker
				if (enableTime && instance.input['_hasTimeChanges'])
				{
					// get current values from the time input fields (dateStr may be stale)
					const hourInput = instance.calendarContainer.querySelector('.flatpickr-hour');
					const minuteInput = instance.calendarContainer.querySelector('.flatpickr-minute');
					const ampmInput = instance.calendarContainer.querySelector('.flatpickr-am-pm');
					if (hourInput && minuteInput)
					{
						let hour = parseInt(hourInput.value, 10);
						const minute = parseInt(minuteInput.value, 10);
						// Handle AM/PM for 12-hour mode
						if (ampmInput && !pFlatpickrOptions.Time24Hour)
						{
							const isPM = ampmInput.textContent === 'PM';
							// Convert 12-hour to 24-hour format
							if (isPM && hour !== 12)
							{
								hour = hour + 12;
							}
							else if (!isPM && hour === 12)
							{
								hour = 0;
							}
						}
						// Create a fake-local Date with the manually entered time
						// (components represent document-TZ values since the picker operates in that context)
						const baseDate = selectedDates[0] || new Date();
						const newDate = new Date(
							baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(),
							hour, minute, 0, 0,
						);
						// Set date without triggering onChange - onDataRequest will use fromFlatpickrDate
						instance.setDate(newDate, false);
						// Dispatch change event to trigger onDataRequest
						const tmpChangeEvent = new Event('change', { bubbles: true });
						instance.input.dispatchEvent(tmpChangeEvent);
					}
					instance.input['_hasTimeChanges'] = false;
				}
				// Reset _userCleared after close so the next open of an empty picker
				// can seed the UI preview with "now" again
				instance.input['_userCleared'] = false;
			},
		};
		// initialize the flatpickr plugin
		window.flatpickr(tmpHTMLID, tmpConfig);
	}

	// TODO: Expand this to support more DayJS to Flatpickr format conversions as needed
	/**
	 * Convert a DayJS date format string to a Flatpickr date format string.
	 * @param {string} pDayJSFormat The DayJS date format string.
	 * @return {string} The converted Flatpickr date format string.
	 */
	convertDayJSToFlatpickrFormat(pDayJSFormat)
	{
		// simple mapping for common formats
		// TODO: expand this mapping as needed or implement a more robust conversion
		const formatMappings = {
			'YYYY-MM-DD': 'Y-m-d',
			'YYYY-MM-DD HH:mm': 'Y-m-d H:i',
			'YYYY-MM-DD HH:mm:ss': 'Y-m-d H:i:S',
			'YYYY-MM-DD HH:mm a': 'Y-m-d H:i K',
			'YYYY-MM-DD hh:mm a': 'Y-m-d h:i K',
			'YYYY-MM-DD HH:mm A': 'Y-m-d H:i K',
			'MM/DD/YYYY': 'm/d/Y',
			'MM/DD/YYYY HH:mm': 'm/d/Y H:i',
			'MM/DD/YYYY HH:mm:ss': 'm/d/Y H:i:S',
			'MM/DD/YYYY HH:mm a': 'm/d/Y H:i K',
			'MM/DD/YYYY hh:mm a': 'm/d/Y h:i K',
			'MM/DD/YYYY HH:mm A': 'm/d/Y H:i K',
			'HH:mm:ss': 'H:i:S',
			'HH:mm': 'H:i',
			'hh:mm': 'h:i',
			'HH:mm a': 'H:i K',
			'HH:mm A': 'H:i K',
			'hh:mm a': 'h:i K',
			'hh:mm A': 'h:i K',
			'hh:mm:ss a': 'h:i:S K',
			'hh:mm:ss A': 'h:i:S K',
		};
		if (formatMappings[pDayJSFormat])
		{
			return formatMappings[pDayJSFormat];
		}
		// if no mapping found, return the original format (may not work correctly)
		console.warn(`No Flatpickr format mapping found for DayJS format: ${pDayJSFormat}. Using original format.`);
		console.info('Consider expanding the formatMappings in convertDayJSToFlatpickrFormat() for better compatibility.');
		return pDayJSFormat;
	}

	/**
	 * Safely resolve a timezone string, falling back to the document timezone option,
	 * and ultimately to undefined (which causes dayjs.tz() to use the browser-local
	 * zone instead of throwing). An invalid or unrecognized timezone otherwise causes
	 * dayjs.tz() to throw, and Flatpickr's init swallows that exception silently —
	 * so callers should always route through this helper.
	 *
	 * @param {string} [pTimezone] - Candidate timezone string (e.g., 'America/Chicago')
	 * @return {string|undefined} A validated timezone string, or undefined to mean "browser local"
	 */
	resolveTimezone(pTimezone)
	{
		const candidate = pTimezone || this.fable?.options?.Timezone;
		if (!candidate)
		{
			return undefined;
		}
		try
		{
			// Intl rejects unknown zones with a RangeError; this is the canonical validity check
			new Intl.DateTimeFormat('en-US', { timeZone: candidate });
			return candidate;
		}
		catch (err)
		{
			console.warn(`FlatpickrUtilProvider: invalid timezone "${candidate}", falling back to browser local. Reason: ${err?.message}`);
			return undefined;
		}
	}

	/**
	 * Convert an ISO string (or special value) to a Date object whose local components
	 * match the document-timezone representation. This "tricks" Flatpickr into displaying
	 * document-TZ time as if it were browser-local time, so the user sees the correct
	 * document-context date/time without any post-hoc shifting.
	 *
	 * @param {string|Date|null} pISOValue - ISO string, Date object, 'now', 'today', or null/empty
	 * @param {string} [pDocumentTZ] - Document timezone (e.g., 'America/Chicago')
	 * @return {Date|null} A Date whose local components match document-TZ values, or null if invalid
	 */
	toFlatpickrDate(pISOValue, pDocumentTZ)
	{
		if (!pISOValue)
		{
			return null;
		}

		const tmpTimezone = this.resolveTimezone(pDocumentTZ);

		// Handle 'now' / 'today' special values
		if (this.isSpecialDefaultValue(pISOValue))
		{
			const nowInDocTZ = this.pict.Dates.dayJS().tz(tmpTimezone);
			return new Date(
				nowInDocTZ.year(), nowInDocTZ.month(), nowInDocTZ.date(),
				nowInDocTZ.hour(), nowInDocTZ.minute(), 0, 0,
			);
		}

		const parsed = this.pict.Dates.dayJS(pISOValue);
		if (!parsed.isValid())
		{
			return null;
		}

		// Convert to document timezone and extract components
		const inDocTZ = parsed.tz(tmpTimezone);

		// Create a fake-local Date whose .getHours(), .getDate(), etc. match document-TZ values
		// Flatpickr will display these as-is since it assumes everything is browser-local
		return new Date(
			inDocTZ.year(), inDocTZ.month(), inDocTZ.date(),
			inDocTZ.hour(), inDocTZ.minute(), inDocTZ.second(), inDocTZ.millisecond(),
		);
	}

	/**
	 * Convert a Date picked by Flatpickr back to an ISO string, interpreting its local
	 * components as document-timezone values (since we fed Flatpickr a fake-local Date
	 * via toFlatpickrDate). This is the inverse of toFlatpickrDate.
	 *
	 * @param {Date} pFlatpickrDate - Date object from Flatpickr (browser-local interpretation)
	 * @param {string} pDocumentTZ - Document timezone (e.g., 'America/Chicago')
	 * @param {string} [pPickerType='datetime'] - 'date', 'time', or 'datetime'
	 * @return {string} ISO string representing the picked moment in document-TZ context
	 */
	fromFlatpickrDate(pFlatpickrDate, pDocumentTZ, pPickerType = 'datetime')
	{
		if (!pFlatpickrDate || !(pFlatpickrDate instanceof Date) || isNaN(pFlatpickrDate.getTime()))
		{
			return '';
		}

		const tmpTimezone = this.resolveTimezone(pDocumentTZ);

		// Read the components the user actually saw in the Flatpickr UI
		const y = pFlatpickrDate.getFullYear();
		const m = pFlatpickrDate.getMonth() + 1; // JS Date is 0-indexed
		const d = pFlatpickrDate.getDate();
		const h = pFlatpickrDate.getHours();
		const min = pFlatpickrDate.getMinutes();
		const sec = pFlatpickrDate.getSeconds();

		// For date-only pickers, zero out the time to avoid DST edge cases
		const timeH = (pPickerType === 'date') ? 0 : h;
		const timeM = (pPickerType === 'date') ? 0 : min;
		const timeS = (pPickerType === 'date') ? 0 : sec;

		// Build a date-time string and interpret it as document-TZ. The static dayjs.tz()
		// form throws on an undefined timezone (unlike the instance .tz()), so when
		// resolveTimezone falls through to undefined we parse as browser-local instead.
		const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')} ${String(timeH).padStart(2, '0')}:${String(timeM).padStart(2, '0')}:${String(timeS).padStart(2, '0')}`;
		const interpreted = tmpTimezone ?
			this.pict.Dates.dayJS.tz(dateStr, 'YYYY-MM-DD HH:mm:ss', tmpTimezone) :
			this.pict.Dates.dayJS(dateStr, 'YYYY-MM-DD HH:mm:ss');

		if (!interpreted.isValid())
		{
			return '';
		}

		return interpreted.toISOString();
	}

	/**
	 * Determine whether a value is one of the logical "live" default tokens
	 * ('now', 'today') rather than a concrete date value. Matching is trimmed and
	 * case-insensitive so configuration authors aren't tripped up by casing.
	 *
	 * @param {any} pValue - Candidate value (typically a descriptor's `Default` or a marshaled model value)
	 * @return {boolean} True when the value is a recognized special default token
	 */
	isSpecialDefaultValue(pValue)
	{
		return (typeof pValue === 'string') && SPECIAL_DEFAULT_VALUES.includes(pValue.trim().toLowerCase());
	}

	/**
	 * Resolve a logical default token ('now'/'today') to the concrete ISO string that
	 * would have been stored had the user picked that moment. Composes the same
	 * fake-local round-trip (toFlatpickrDate → fromFlatpickrDate) the interactive
	 * onDataRequest path uses, so a locked-in default is byte-for-byte consistent with
	 * a user-entered value for the same instant. Returns '' for non-special values.
	 *
	 * @param {any} pValue - The value to resolve (only special tokens produce a result)
	 * @param {string} [pTimezone] - Document timezone (e.g., 'America/Chicago')
	 * @param {string} [pPickerType='datetime'] - 'date', 'time', or 'datetime'
	 * @return {string} The resolved ISO string, or '' if the value is not a special token
	 */
	resolveSpecialDefaultToISO(pValue, pTimezone, pPickerType = 'datetime')
	{
		if (!this.isSpecialDefaultValue(pValue))
		{
			return '';
		}
		const tmpFakeLocalDate = this.toFlatpickrDate(pValue, pTimezone);
		return this.fromFlatpickrDate(tmpFakeLocalDate, pTimezone, pPickerType);
	}

	/**
	 * @deprecated Use fromFlatpickrDate() instead. Kept for backward compatibility during migration.
	 * Convert parsed local time to ISO string, adjusting for timezone context.
	 * For edits (storedValue exists), interprets input as record timezone.
	 * For initial entry, interprets input as browser local time.
	 * @param {object} pLocalParsed dayJS parsed object (browser local interpretation)
	 * @param {string} pStoredValue existing ISO value (empty/null for initial entry)
	 * @param {string} pTimezone record timezone (e.g., 'America/Chicago')
	 * @return {string} ISO string
	 */
	convertToISOWithTimezoneContext(pLocalParsed, pStoredValue, pTimezone)
	{
		if (pStoredValue && typeof pStoredValue === 'string' && pStoredValue.length > 10)
		{
			// Edit
			const browserTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
			const browserOffset = pLocalParsed.tz(browserTimezone).utcOffset();
			const recordOffset = pLocalParsed.tz(pTimezone).utcOffset();
			const offsetDiffMinutes = recordOffset - browserOffset;
			return pLocalParsed.subtract(offsetDiffMinutes, 'minute').toISOString();
		}
		// Initial entry
		return pLocalParsed.toISOString();
	}

	/**
	 * Clear the date time picker.
	 * @param {string} pInputHTMLID The input HTML ID.
	 * @return {void}
	 */
	clearDatePicker(pInputHTMLID)
	{
		const tmpFlatpickrElement = document.querySelector(pInputHTMLID);
		if (tmpFlatpickrElement)
		{
			const tmpFlatpickrInstance = tmpFlatpickrElement['_flatpickr'];
			if (tmpFlatpickrInstance)
			{
				tmpFlatpickrInstance.clear();
				// Mark as explicitly cleared so onOpen won't re-seed with "now"
				tmpFlatpickrElement['_userCleared'] = true;
				// Reset time changes flag so onClose doesn't re-apply stale hour/minute values
				tmpFlatpickrElement['_hasTimeChanges'] = false;
				// Clear hidden input so next entry is treated as initial (not edit)
				const tmpHiddenID = tmpFlatpickrElement['_hiddenInputID'];
				if (tmpHiddenID)
				{
					this.pict.ContentAssignment.assignContent(tmpHiddenID, '');
				}
				// create a new change event to trigger the data request
				const tmpChangeEvent = new Event('change', { bubbles: true });
				tmpFlatpickrElement.dispatchEvent(tmpChangeEvent);
				tmpFlatpickrInstance.close();
			}
		}
	}

	/**
	 * Format a date value with the given format and timezone. Usually, the timezone is set for the document record, but can be overridden.
	 * @param {string|Date} pDateValue The date value to format.
	 * @param {string} pDayJSDateFormat The date format string. (assumes DayJS formatting)
	 * @param {string} [pTimezone] (optional) The timezone to use for formatting.
	 * @param {boolean} [isTimeOnly] (optional) Whether the value is time-only. Defaults to false. If true, the date portion will be applied to today's date.
	 * @return {string} The formatted date string.
	 */
	formatDateWithTimezone(pDateValue, pDayJSDateFormat, pTimezone, isTimeOnly = false)
	{
		if (!pDateValue)
		{
			return '';
		}
		if (!pDayJSDateFormat)
		{
			console.error('DayJS date format is required for formatting the date value.');
			return '';
		}
		// use the provided timezone or fall back to the document timezone
		const tmpTimezone = this.resolveTimezone(pTimezone);
		// handle special 'now' or 'today' values
		if (this.isSpecialDefaultValue(pDateValue))
		{
			if (isTimeOnly)
			{
				// for time-only, combine today's date with the current time to make it a valid date for Flatpickr (will be saved as ISO time later)
				const tmpTodayDate = this.pict.Dates.dayJS().tz(tmpTimezone).format('YYYY-MM-DD');
				const tmpCurrentTime = this.pict.Dates.dayJS().tz(tmpTimezone).format(pDayJSDateFormat);
				return this.pict.Dates.dayJS(tmpTodayDate + ' ' + tmpCurrentTime).tz(tmpTimezone).format('YYYY-MM-DD ' + pDayJSDateFormat);
			}
			// for date or datetime, just return the current date/time in the specified timezone
			return this.pict.Dates.dayJS().tz(tmpTimezone).format(pDayJSDateFormat);
		}
		// validate the date value
		if (this.pict.Dates.dayJS(pDateValue).isValid() === false)
		{
			console.error(`Invalid date value provided: ${pDateValue}`);
			return '';
		}
		if ((typeof pDateValue == 'string' || pDateValue instanceof String) && pDateValue?.length == 10)
		{
			return this.pict.Dates.dayJS(pDateValue).format(pDayJSDateFormat);
		}
		return this.pict.Dates.dayJS(pDateValue).tz(tmpTimezone).format(pDayJSDateFormat);
	}

	/**
	 * Set the date time picker to the 'now' date/time.
	 * @param {string} pInputHTMLID The input HTML ID.
	 * @return {void}
	 */
	setDatePickerToNow(pInputHTMLID)
	{
		const tmpFlatpickrElement = document.querySelector(pInputHTMLID);
		if (tmpFlatpickrElement)
		{
			const tmpFlatpickrInstance = tmpFlatpickrElement['_flatpickr'];
			if (tmpFlatpickrInstance)
			{
				// Clear hidden input so the next save writes fresh
				const tmpHiddenID = tmpFlatpickrElement['_hiddenInputID'];
				if (tmpHiddenID)
				{
					this.pict.ContentAssignment.assignContent(tmpHiddenID, '');
				}
				// Create a fake-local Date representing "now" in document timezone
				const tmpTimezone = this.resolveTimezone();
				const nowInDocTZ = this.pict.Dates.dayJS().tz(tmpTimezone);
				const fakeLocalNow = new Date(
					nowInDocTZ.year(), nowInDocTZ.month(), nowInDocTZ.date(),
					nowInDocTZ.hour(), nowInDocTZ.minute(), 0, 0,
				);
				tmpFlatpickrInstance.setDate(fakeLocalNow);
				// "Set to Current" commits a real value. setDate() above runs with
				// triggerChange=false (no onChange), so clear the onOpen "now" preview
				// flag (and any pending manual-time flag) ourselves — otherwise the
				// trailing close() -> onClose sees _previewOnly and reverts the value we
				// just set, so it would only reappear on a later marshal.
				tmpFlatpickrElement['_previewOnly'] = false;
				tmpFlatpickrElement['_hasTimeChanges'] = false;
				// create a new change event to trigger the data request
				const tmpChangeEvent = new Event('change', { bubbles: true });
				tmpFlatpickrElement.dispatchEvent(tmpChangeEvent);
				tmpFlatpickrInstance.close();
			}
		}
	}
}

module.exports = FlatpickrUtilProvider;
